import {
  useEffect,
  useImperativeHandle,
  useRef,
  forwardRef,
  useState,
} from 'react';
import { Point, dist } from '../lib/geometry';
import { usePinchPan } from '../hooks/usePinchPan';
import { useViewportResize } from '../hooks/useViewportResize';

export type DistanceMode = 'measure' | 'calibrate';

export interface CalibrationPoints {
  p1: Point;
  p2: Point;
}

export interface DistanceCanvasHandle {
  flatten: () => HTMLCanvasElement;
  reset: () => void;
}

interface Props {
  /** Captured frame in intrinsic resolution. */
  source: HTMLCanvasElement;
  /** measure = drag the two cyan points; calibrate = drag the two orange ones. */
  mode: DistanceMode;
  /** Reference length entered by the user (mm). Used together with the calibration line. */
  realMm: number;
  /** Called whenever the calibration points are first placed or moved. */
  onCalibrationChange?: (cal: CalibrationPoints | null) => void;
  /** Live distance, both raw px and (if calibrated) mm. */
  onDistanceChange?: (px: number, mm: number | null) => void;
  colorMeasure?: string;
  colorCalibration?: string;
}

/* ─── Drag keys ─── */
type DragKey = 'm1' | 'm2' | 'c1' | 'c2';

const HANDLE_OUTER = 11;
const HANDLE_DOT = 2.5;
const HIT_RADIUS = 34;

const DistanceCanvas = forwardRef<DistanceCanvasHandle, Props>(
  function DistanceCanvas(
    {
      source,
      mode,
      realMm,
      onCalibrationChange,
      onDistanceChange,
      colorMeasure = '#7cc4ff',
      colorCalibration = '#ff9f0a',
    },
    ref,
  ) {
    const containerRef = useRef<HTMLDivElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const dragRef = useRef<DragKey | null>(null);

    // Re-render trigger for viewport changes (rotation, standalone vs tab).
    const viewportTick = useViewportResize();

    const [measure, setMeasure] = useState<{ p1: Point; p2: Point }>(() =>
      initialMeasure(source.width, source.height),
    );
    const [calibration, setCalibration] = useState<CalibrationPoints | null>(null);

    const measureRef = useRef(measure);
    const calRef = useRef<CalibrationPoints | null>(calibration);
    measureRef.current = measure;
    calRef.current = calibration;

    const { transform, handlers, reset: resetTransform } = usePinchPan({
      minScale: 1,
      maxScale: 8,
      panSinglePointer: false,
    });

    /* When the user enters calibration mode for the first time, auto-initialise
       the calibration line in the centre of the image so they don't have to hunt
       for invisible handles. */
    useEffect(() => {
      if (mode === 'calibrate' && !calRef.current) {
        const cal = initialCalibration(source.width, source.height);
        setCalibration(cal);
        onCalibrationChange?.(cal);
      }
    }, [mode, source, onCalibrationChange]);

    /* ─── Coordinate conversion ─── */
    const screenToImage = (sx: number, sy: number): Point => {
      const rect = containerRef.current!.getBoundingClientRect();
      const fit = fitContain(source.width, source.height, rect.width, rect.height);
      const cssX = (sx - rect.left - transform.tx) / transform.scale - fit.x;
      const cssY = (sy - rect.top - transform.ty) / transform.scale - fit.y;
      return {
        x: cssX * (source.width / fit.w),
        y: cssY * (source.height / fit.h),
      };
    };

    /* ─── Draw loop ─── */
    useEffect(() => {
      const canvas = canvasRef.current;
      const container = containerRef.current;
      if (!canvas || !container) return;

      const dpr = Math.min(window.devicePixelRatio || 1, 3);
      const rect = container.getBoundingClientRect();
      const cssW = rect.width;
      const cssH = rect.height;

      canvas.width = Math.round(cssW * dpr);
      canvas.height = Math.round(cssH * dpr);
      canvas.style.width = `${cssW}px`;
      canvas.style.height = `${cssH}px`;

      const ctx = canvas.getContext('2d')!;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      ctx.fillStyle = '#000';
      ctx.fillRect(0, 0, cssW, cssH);

      const fit = fitContain(source.width, source.height, cssW, cssH);
      ctx.save();
      ctx.translate(transform.tx, transform.ty);
      ctx.scale(transform.scale, transform.scale);
      ctx.drawImage(source, fit.x, fit.y, fit.w, fit.h);

      const toCss = (p: Point) => ({
        x: fit.x + (p.x / source.width) * fit.w,
        y: fit.y + (p.y / source.height) * fit.h,
      });

      const dim = mode === 'calibrate';

      // Measurement line — drawn dimmed when user is calibrating
      drawDistanceLine(
        ctx,
        toCss(measure.p1),
        toCss(measure.p2),
        colorMeasure,
        transform.scale,
        dim ? 0.35 : 1,
      );
      drawHandle(ctx, toCss(measure.p1), colorMeasure, transform.scale, dim ? 0.4 : 1);
      drawHandle(ctx, toCss(measure.p2), colorMeasure, transform.scale, dim ? 0.4 : 1);

      // Calibration line — visible when present, prominent during calibration
      if (calibration) {
        drawDistanceLine(
          ctx,
          toCss(calibration.p1),
          toCss(calibration.p2),
          colorCalibration,
          transform.scale,
          mode === 'calibrate' ? 1 : 0.5,
        );
        drawHandle(
          ctx,
          toCss(calibration.p1),
          colorCalibration,
          transform.scale,
          mode === 'calibrate' ? 1 : 0.5,
        );
        drawHandle(
          ctx,
          toCss(calibration.p2),
          colorCalibration,
          transform.scale,
          mode === 'calibrate' ? 1 : 0.5,
        );
      }

      ctx.restore();
    }, [measure, calibration, mode, transform, source, colorMeasure, colorCalibration, viewportTick]);

    /* ─── Distance reporting ─── */
    useEffect(() => {
      const px = dist(measure.p1, measure.p2);
      const mm = mmFor(px, calibration, realMm);
      onDistanceChange?.(px, mm);
    }, [measure, calibration, realMm, onDistanceChange]);

    /* ─── Pointer handling ─── */
    const onPointerDown = (e: React.PointerEvent) => {
      const img = screenToImage(e.clientX, e.clientY);
      const rect = containerRef.current!.getBoundingClientRect();
      const fit = fitContain(source.width, source.height, rect.width, rect.height);
      const hitImgRadius =
        (HIT_RADIUS / transform.scale) * (source.width / fit.w);

      // Only the active set is grabbable.
      const cands: { key: DragKey; pt: Point }[] =
        mode === 'measure'
          ? [
              { key: 'm1', pt: measureRef.current.p1 },
              { key: 'm2', pt: measureRef.current.p2 },
            ]
          : calRef.current
            ? [
                { key: 'c1', pt: calRef.current.p1 },
                { key: 'c2', pt: calRef.current.p2 },
              ]
            : [];

      let best: DragKey | null = null;
      let bestD = hitImgRadius;
      for (const c of cands) {
        const d = dist(c.pt, img);
        if (d < bestD) {
          bestD = d;
          best = c.key;
        }
      }

      if (best) {
        dragRef.current = best;
        (e.target as Element).setPointerCapture?.(e.pointerId);
        return;
      }
      handlers.onPointerDown(e);
    };

    const onPointerMove = (e: React.PointerEvent) => {
      if (dragRef.current) {
        const img = screenToImage(e.clientX, e.clientY);
        const k = dragRef.current;
        if (k === 'm1' || k === 'm2') {
          setMeasure((cur) => (k === 'm1' ? { ...cur, p1: img } : { ...cur, p2: img }));
        } else {
          const cur = calRef.current;
          if (!cur) return;
          const next = k === 'c1' ? { ...cur, p1: img } : { ...cur, p2: img };
          setCalibration(next);
          onCalibrationChange?.(next);
        }
        return;
      }
      handlers.onPointerMove(e);
    };

    const onPointerUp = (e: React.PointerEvent) => {
      if (dragRef.current) {
        dragRef.current = null;
        return;
      }
      handlers.onPointerUp(e);
    };

    /* ─── Imperative API ─── */
    useImperativeHandle(ref, () => ({
      flatten: () =>
        flattenToCanvas(
          source,
          measureRef.current,
          calRef.current,
          realMm,
          colorMeasure,
          colorCalibration,
        ),
      reset: () => {
        setMeasure(initialMeasure(source.width, source.height));
        setCalibration(null);
        onCalibrationChange?.(null);
        resetTransform();
      },
    }));

    return (
      <div
        ref={containerRef}
        style={{
          position: 'absolute',
          inset: 0,
          background: '#000',
          overflow: 'hidden',
          touchAction: 'none',
        }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        <canvas ref={canvasRef} style={{ display: 'block' }} />
      </div>
    );
  },
);

export default DistanceCanvas;

/* ════════════ helpers ════════════ */

function initialMeasure(w: number, h: number) {
  const cx = w / 2;
  const cy = h / 2;
  const r = Math.min(w, h) * 0.22;
  return {
    p1: { x: cx - r, y: cy },
    p2: { x: cx + r, y: cy },
  };
}

function initialCalibration(w: number, h: number): CalibrationPoints {
  // Place the calibration line slightly above the measurement so they don't
  // overlap on first appearance.
  const cx = w / 2;
  const cy = h * 0.35;
  const r = Math.min(w, h) * 0.12;
  return {
    p1: { x: cx - r, y: cy },
    p2: { x: cx + r, y: cy },
  };
}

function fitContain(srcW: number, srcH: number, dstW: number, dstH: number) {
  const s = Math.min(dstW / srcW, dstH / srcH);
  const w = srcW * s;
  const h = srcH * s;
  return { x: (dstW - w) / 2, y: (dstH - h) / 2, w, h };
}

function mmFor(
  px: number,
  cal: CalibrationPoints | null,
  realMm: number,
): number | null {
  if (!cal || realMm <= 0) return null;
  const calPx = dist(cal.p1, cal.p2);
  if (calPx <= 0) return null;
  return px * (realMm / calPx);
}

function drawDistanceLine(
  ctx: CanvasRenderingContext2D,
  a: Point,
  b: Point,
  color: string,
  scale: number,
  alpha: number,
) {
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.lineCap = 'round';

  // Backing stroke for contrast.
  ctx.strokeStyle = 'rgba(0,0,0,0.55)';
  ctx.lineWidth = 3.5 / scale;
  ctx.beginPath();
  ctx.moveTo(a.x, a.y);
  ctx.lineTo(b.x, b.y);
  ctx.stroke();

  // Coloured line.
  ctx.strokeStyle = color;
  ctx.lineWidth = 1.6 / scale;
  ctx.beginPath();
  ctx.moveTo(a.x, a.y);
  ctx.lineTo(b.x, b.y);
  ctx.stroke();

  // Endpoint ticks perpendicular to the line — visual hint of "measurement caliper".
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len = Math.hypot(dx, dy);
  if (len > 0) {
    const nx = -dy / len;
    const ny = dx / len;
    const tick = 7 / scale;
    drawTick(ctx, a, nx, ny, tick);
    drawTick(ctx, b, nx, ny, tick);
  }
  ctx.restore();
}

function drawTick(
  ctx: CanvasRenderingContext2D,
  p: Point,
  nx: number,
  ny: number,
  tick: number,
) {
  ctx.beginPath();
  ctx.moveTo(p.x - nx * tick, p.y - ny * tick);
  ctx.lineTo(p.x + nx * tick, p.y + ny * tick);
  ctx.stroke();
}

function drawHandle(
  ctx: CanvasRenderingContext2D,
  p: Point,
  color: string,
  scale: number,
  alpha: number,
) {
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.fillStyle = 'rgba(0,0,0,0.35)';
  ctx.beginPath();
  ctx.arc(p.x, p.y, (HANDLE_OUTER + 1.5) / scale, 0, Math.PI * 2);
  ctx.fill();

  ctx.strokeStyle = color;
  ctx.lineWidth = 1.5 / scale;
  ctx.beginPath();
  ctx.arc(p.x, p.y, HANDLE_OUTER / scale, 0, Math.PI * 2);
  ctx.stroke();

  ctx.fillStyle = '#fff';
  ctx.beginPath();
  ctx.arc(p.x, p.y, (HANDLE_DOT + 1.2) / scale, 0, Math.PI * 2);
  ctx.fill();

  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.arc(p.x, p.y, HANDLE_DOT / scale, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

/* ─── Export ─── */

function flattenToCanvas(
  source: HTMLCanvasElement,
  measure: { p1: Point; p2: Point },
  cal: CalibrationPoints | null,
  _realMm: number,
  colorMeasure: string,
  colorCalibration: string,
): HTMLCanvasElement {
  // Returns the captured frame with the measurement (and calibration if set)
  // drawn on top — clean, no labels or watermark. Branding lives in the
  // unified report frame (see buildHeightReport).
  const out = document.createElement('canvas');
  out.width = source.width;
  out.height = source.height;
  const ctx = out.getContext('2d')!;
  ctx.drawImage(source, 0, 0);

  if (cal) {
    drawDistanceLine(ctx, cal.p1, cal.p2, colorCalibration, 1, 0.6);
    drawHandle(ctx, cal.p1, colorCalibration, 1, 0.6);
    drawHandle(ctx, cal.p2, colorCalibration, 1, 0.6);
  }
  drawDistanceLine(ctx, measure.p1, measure.p2, colorMeasure, 1, 1);
  drawHandle(ctx, measure.p1, colorMeasure, 1, 1);
  drawHandle(ctx, measure.p2, colorMeasure, 1, 1);

  return out;
}
